import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";

const PatientVerificationDialog = ({
  open,
  t,
  nameValue,
  dobValue,
  onNameChange,
  onDobChange,
  onSubmit,
  onCancel,
  error,
  loading,
}) => {
  const nameRef = useRef(null);
  const dobRef = useRef(null);

  // When the dialog opens, focus DOB if name is already populated, otherwise focus name
  useEffect(() => {
    if (!open) return;
    // Small timeout lets the Dialog finish its open animation before focusing
    const timer = setTimeout(() => {
      if (nameValue && nameValue.trim() && dobValue && dobValue.trim()) {
        nameRef.current && nameRef.current.focus();
      } else if (nameValue && nameValue.trim()) {
        dobRef.current && dobRef.current.focus();
      } else {
        nameRef.current && nameRef.current.focus();
      }
    }, 50);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const handleNameInput = (event) => {
    onNameChange(event.target.value);
  };

  const formatDobValue = (value) => {
    if (!value) {
      return "";
    }

    const digitsOnly = value.replace(/\D/g, "").slice(0, 8);
    const parts = [];

    if (digitsOnly.length >= 2) {
      parts.push(digitsOnly.slice(0, 2));
    } else if (digitsOnly.length > 0) {
      parts.push(digitsOnly);
    }

    if (digitsOnly.length >= 4) {
      parts.push(digitsOnly.slice(2, 4));
    } else if (digitsOnly.length > 2) {
      parts.push(digitsOnly.slice(2));
    }

    if (digitsOnly.length > 4) {
      parts.push(digitsOnly.slice(4));
    }

    return parts.join("/");
  };

  const handleDobInput = (event) => {
    const formatted = formatDobValue(event.target.value || "");
    onDobChange(formatted);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !loading && onSubmit) {
      event.preventDefault();
      onSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 18px 40px rgba(15, 23, 42, 0.24)",
          border: "1px solid #e2e8f0",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: "1.15rem",
          pb: 1,
          pt: 2.5,
          px: 3,
          background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
          color: "#ffffff",
        }}
      >
        {t("Console.Counter.VerifyPatientTitle", {
          defaultValue: "Verify patient",
        })}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          px: 3,
          py: 3,
          backgroundColor: "#f8fafc",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.95rem",
            color: "#475569",
            lineHeight: 1.55,
          }}
        >
          {t("Console.Counter.VerifyPatientInstruction", {
            defaultValue: "Enter the patient's name and date of birth to continue.",
          })}
        </Typography>
        <TextField
          value={nameValue}
          onChange={handleNameInput}
          onKeyDown={handleKeyDown}
          placeholder={t("Console.Counter.PatientNamePlaceholder", {
            defaultValue: "Enter full name",
          })}
          inputRef={nameRef}
          disabled={loading}
          fullWidth
          size="small"
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              "& input::placeholder": {
                color: "#94a3b8",
                opacity: 1,
              },
            },
          }}
        />
        <TextField
          value={dobValue}
          onChange={handleDobInput}
          onKeyDown={handleKeyDown}
          placeholder={t("Console.Counter.DobPlaceholder", {
            defaultValue: "MM/DD/YYYY",
          })}
          inputRef={dobRef}
          disabled={loading}
          fullWidth
          size="small"
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              "& input::placeholder": {
                color: "#94a3b8",
                opacity: 1,
              },
            },
          }}
          FormHelperTextProps={{
            sx: {
              mt: 0.75,
              color: "#64748b",
              fontSize: "0.75rem",
              fontWeight: 500,
            },
          }}
        />
        {error ? (
          <Box
            sx={{
              mt: 1,
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 2,
              px: 2.5,
              py: 1.25,
            }}
          >
            <Typography
              sx={{
                color: "#b91c1c",
                fontSize: "0.85rem",
                fontWeight: 600,
              }}
            >
              {error}
            </Typography>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: "#f1f5f9",
          borderTop: "1px solid #e2e8f0",
          gap: 1,
        }}
      >
        <Button
          variant="text"
          onClick={onCancel}
          disabled={loading}
          sx={{
            textTransform: "none",
            color: "#475569",
            fontWeight: 600,
            px: 1.5,
          }}
        >
          {t("Global.Cancel", { defaultValue: "Cancel" })}
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={loading}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: "#7c3aed",
            borderRadius: 2,
            boxShadow: "0 8px 18px rgba(124, 58, 237, 0.35)",
            "&:hover": {
              backgroundColor: "#6d28d9",
              boxShadow: "0 10px 22px rgba(124, 58, 237, 0.45)",
            },
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircularProgress size={18} sx={{ color: "#ede9fe" }} />
              <Typography
                component="span"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  color: "inherit",
                }}
              >
                {t("Console.Counter.Verifying", { defaultValue: "Verifying" })}
              </Typography>
            </Box>
          ) : (
            t("Console.Counter.Verify", { defaultValue: "Verify" })
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PatientVerificationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  nameValue: PropTypes.string.isRequired,
  dobValue: PropTypes.string.isRequired,
  onNameChange: PropTypes.func.isRequired,
  onDobChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  error: PropTypes.string,
  loading: PropTypes.bool,
};

PatientVerificationDialog.defaultProps = {
  error: "",
  loading: false,
};

export default PatientVerificationDialog;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/components/PatientVerificationDialog.jsx